import * as React from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import {
  useGetJobLogsPageQuery,
  type GetJobLogsPageArg
} from '@axilon-apps/domains-ontology-ui-state'
import { useDebouncedValue } from '../../lib/use-debounced-value'
import { cn } from '../../lib/utils'

const LOGS_PER_PAGE = 50
const SEARCH_DEBOUNCE_MS = 200

export interface JobLogEntry {
  at: string
  level: 'info' | 'error'
  message: string
}

function formatLogTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  const ms = String(d.getMilliseconds()).padStart(3, '0')
  return `${hh}:${mm}:${ss}.${ms}`
}

/** Split a log message into its one-line summary and an optional expandable body
 * (everything after the first newline — e.g. a written script, stdout, or file
 * contents the agent read). */
function splitMessage(message: string): {
  summary: string
  body: string | null
} {
  const nl = message.indexOf('\n')
  if (nl === -1) return { summary: message, body: null }
  return { summary: message.slice(0, nl), body: message.slice(nl + 1) }
}

interface JobLogsProps {
  jobId: string
  /** Shown when there are no logs at all. */
  emptyHint?: string
  /** When true, multiline log bodies start expanded (and new ones stay expanded). */
  defaultExpanded?: boolean
}

/**
 * Shared job-log timeline: the filterable, monospace log view from the ingest
 * jobs table, with each line that carries extra detail (a multi-line message —
 * script body, stdout, file contents) collapsed behind a click-to-expand
 * dropdown. Used by the IngestPage detail panel and the agent-ingest job page.
 */
export function JobLogs({
  jobId,
  emptyHint = 'No logs recorded yet.',
  defaultExpanded = false
}: JobLogsProps) {
  const [search, setSearch] = React.useState('')
  const query = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(() => new Set())
  // undefined follows the newest logs. Once the user navigates backward,
  // pageEnd is an absolute offset so appends do not move rows while they read.
  const [requestedPageEnd, setRequestedPageEnd] = React.useState<number | undefined>()
  const request = React.useMemo<GetJobLogsPageArg>(
    () => ({
      jobId,
      pageSize: LOGS_PER_PAGE,
      ...(requestedPageEnd === undefined ? {} : { pageEnd: requestedPageEnd }),
      ...(query ? { query } : {})
    }),
    [jobId, query, requestedPageEnd]
  )
  const {
    currentData: page,
    isLoading,
    isFetching
  } = useGetJobLogsPageQuery(request, {
    pollingInterval: 2_000,
    skipPollingIfUnfocused: true
  })

  React.useEffect(() => {
    setSearch('')
    setRequestedPageEnd(undefined)
    setExpanded(new Set())
  }, [jobId])

  const rows = React.useMemo(
    () =>
      (page?.logs ?? []).map((log, index) => ({
        log,
        key: `${log.at}-${(page?.pageStart ?? 0) + index}`,
        ...splitMessage(log.message)
      })),
    [page]
  )

  React.useEffect(() => {
    if (requestedPageEnd !== undefined && page && requestedPageEnd >= page.totalSize) {
      setRequestedPageEnd(undefined)
    }
  }, [page, requestedPageEnd])

  React.useEffect(() => {
    if (!defaultExpanded) return
    setExpanded((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const row of rows) {
        if (row.body !== null && !next.has(row.key)) {
          next.add(row.key)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [defaultExpanded, rows])

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-ink-3"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setRequestedPageEnd(undefined)
          }}
          placeholder="Filter logs…"
          className="h-7 w-full rounded-md border border-line bg-bg-elev pr-2 pl-7 font-mono text-11 text-ink placeholder:text-ink-3 focus:border-ring focus:outline-none"
        />
      </div>
      {!page && (isLoading || isFetching) ? (
        <div className="text-xs text-ink-3">Loading logs…</div>
      ) : !page?.totalSize && !query ? (
        <div className="text-xs text-ink-3">{emptyHint}</div>
      ) : rows.length ? (
        <div
          aria-busy={isFetching}
          className="min-w-0 overflow-hidden rounded-md border border-line bg-bg-elev font-mono text-11 leading-relaxed"
        >
          {rows.map(({ log, key, summary, body }) => {
            const isOpen = expanded.has(key)
            const levelClass = log.level === 'error' ? 'text-st-down' : 'text-st-info'
            return (
              <div key={key}>
                {body !== null ? (
                  <button
                    type="button"
                    onClick={() => toggle(key)}
                    aria-expanded={isOpen}
                    className="grid w-full cursor-pointer grid-cols-[auto_auto_minmax(0,1fr)_auto] items-start gap-x-3 px-3 py-1 text-left hover:bg-bg-sunk"
                  >
                    <span className="tabular-nums text-ink-3">{formatLogTime(log.at)}</span>
                    <span className={cn('uppercase', levelClass)}>{log.level}</span>
                    <span className="min-w-0 break-words text-ink [overflow-wrap:anywhere]">
                      {summary}
                    </span>
                    <ChevronRight
                      className={cn(
                        'mt-0.5 size-3 shrink-0 text-ink-3 transition-transform duration-200 ease-out',
                        isOpen && 'rotate-90'
                      )}
                    />
                  </button>
                ) : (
                  <div className="grid grid-cols-[auto_auto_minmax(0,1fr)] gap-x-3 px-3 py-1 hover:bg-bg-sunk">
                    <span className="tabular-nums text-ink-3">{formatLogTime(log.at)}</span>
                    <span className={cn('uppercase', levelClass)}>{log.level}</span>
                    <span className="min-w-0 break-words whitespace-pre-wrap text-ink [overflow-wrap:anywhere]">
                      {summary}
                    </span>
                  </div>
                )}
                {body !== null && isOpen ? (
                  <pre className="m-0 min-w-0 border-t border-line bg-bg-sunk px-3 py-2 break-words whitespace-pre-wrap text-ink-2 [overflow-wrap:anywhere]">
                    {body}
                  </pre>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-xs text-ink-3">No logs match “{search}”.</div>
      )}
      {(page?.totalSize ?? 0) > LOGS_PER_PAGE ? (
        <nav
          aria-label="Job log pages"
          className="flex items-center justify-between gap-3 text-11 text-ink-3"
        >
          <button
            type="button"
            aria-label="Show older logs"
            disabled={!page || page.pageStart === 0}
            onClick={() => setRequestedPageEnd(page?.pageStart ?? 0)}
            className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md px-2 text-ink-2 hover:bg-bg-elev disabled:pointer-events-none disabled:opacity-40"
          >
            <ChevronLeft aria-hidden className="size-3.5" />
            Older
          </button>
          <span className="tabular-nums" aria-live="polite">
            {(page?.pageStart ?? 0) + 1}–{page?.pageEnd ?? 0} of {page?.totalSize ?? 0}
          </span>
          <button
            type="button"
            aria-label="Show newer logs"
            disabled={!page || page.pageEnd >= page.totalSize}
            onClick={() => {
              if (!page) return
              const nextEnd = page.pageEnd + LOGS_PER_PAGE
              setRequestedPageEnd(nextEnd >= page.totalSize ? undefined : nextEnd)
            }}
            className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md px-2 text-ink-2 hover:bg-bg-elev disabled:pointer-events-none disabled:opacity-40"
          >
            Newer
            <ChevronRight aria-hidden className="size-3.5" />
          </button>
        </nav>
      ) : null}
    </div>
  )
}

export default JobLogs
